import BaseService from "./base.service";
import { getItem, removeItem } from "./localStorage.service";
import { setAuthorizationHeader } from "utils/auth";

class ProtectedService extends BaseService {
  getAuthHeader = () => {
    const accessToken = getItem("x-auth-token");
    if (accessToken) return accessToken;
    return null;
  };

  private buildAuthHeaders = (token: string) => {
    setAuthorizationHeader(token);
    return {
      "x-auth-token": token,
      token,
      Authorization: `Bearer ${token}`,
    };
  };

  makeProtectedRequest = async (fn: any) => {
    try {
      const token = this.getAuthHeader();
      if (!token) throw new Error("No Authorization");
      const authHeaders = this.buildAuthHeaders(token);
      return await fn(authHeaders);
    } catch (e: any) {
      if (
        e.response?.data?.session_expired === 1 ||
        e.response?.data?.exc_type === "PermissionError"
      ) {
        this.clearAccessToken();
      }
      throw e;
    }
  };

  clearAccessToken = () => {
    removeItem("x-auth-token");
    setAuthorizationHeader(null);
    window.dispatchEvent(new Event("storage"));
  };

  getWithParams = async (url: string, config?: any) =>
    this.makeProtectedRequest(async (authHeaders: any) => 
      super.get(url, {
        headers: {
          ...authHeaders,
          ...config,
        },
      })
    );

    postWithParams = async (url: string, data: any, config?: any) =>
      this.makeProtectedRequest(async (authHeaders: any) => {
        const headers: Record<string, string> = {
          ...authHeaders,
          ...(config as Record<string, string> | undefined),
        };
        if (typeof FormData !== "undefined" && data instanceof FormData) {
          delete headers["Content-Type"];
        }
        return super.post(url, data, { headers });
      });
  

  get = async (url: string, config?: any) =>
    this.makeProtectedRequest(async (authHeaders: any) =>
      super.get(url, {
        headers: {
          ...authHeaders,
        },
        ...config,
      })
    );

  post = async (url: string, data?: any, config?: any) =>
    this.makeProtectedRequest(async (authHeaders: any) => {
      const { headers: configHeaders, ...restConfig } = config || {};
      const headers: Record<string, string> = {
        ...authHeaders,
        ...(configHeaders as Record<string, string> | undefined),
      };
      if (
        data != null &&
        typeof FormData !== "undefined" &&
        data instanceof FormData
      ) {
        delete headers["Content-Type"];
      }
      return super.post(url, data, { headers, ...restConfig });
    });

  patch = async (url: string, data?: any, config?: any) =>
    this.makeProtectedRequest(async (authHeaders: any) =>
      super.patch(url, data, {
        headers: {
          ...authHeaders,
        },
        ...config,
      })
    );

  delete = async (url: string, config?: any) =>
    this.makeProtectedRequest(async (authHeaders: any) =>
      super.delete(url, {
        headers: {
          ...authHeaders,
        },
        ...config,
      })
    );
}

export default ProtectedService;
